/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES HUIT PAGES DU SITE, ET LA LISTE EST FERMÉE — `CDC-6` F-69.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **Huit pages, et la liste est fermée.** » Et le cahier des charges tire de cette
 * fermeture deux critères d'acceptation, dont le premier porte sur ce qu'il faut faire de
 * **tout le reste** : « SI une adresse ne correspond à aucune des huit pages, ALORS LE
 * SYSTÈME DOIT rendre la page de refus **sans exécuter aucune requête en base**. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CETTE LISTE EST UNE DONNÉE ET NON UNE SUITE DE `<Route>`.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Écrites une par une dans le routeur, dans le menu et dans le pied de page, les huit
 * adresses vivraient **trois fois**. Une neuvième page apparaîtrait alors sans que rien ne
 * change ailleurs, et **la fermeture de la liste cesserait d'être vérifiable** : il faudrait
 * la relire pour la constater, ce qui est exactement l'état dans lequel une règle se perd.
 *
 * C'est la leçon que `admin/` a payée au LOT-5 sur sa matrice de rôles, et le motif est le
 * même mot pour mot : « une condition enfouie dans un service ne se relit jamais en revue »
 * — la matrice « vit dans un seul fichier, **sous forme de données** », et le code la lit.
 *
 * **Ce que cette forme achète, concrètement** : le routeur déclare exactement ces entrées et
 * rien d'autre ; le menu et le pied de page les **filtrent** au lieu de les recopier ; et un
 * test compte huit lignes. Une page ajoutée sans décision ferait rougir ce compte.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX SURFACES QUI NE SONT PAS DES PAGES DE CETTE LISTE, ET LES DEUX SONT ÉCRITES
 * AILLEURS PARCE QU'ELLES N'EN SONT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **La page de refus**, S-23 du brief de maquette, qui le dit explicitement : « c'est une
 * surface tranchée qui **ne figure pas dans la liste fermée des huit pages**, et c'est écrit
 * plutôt que laissé à découvrir. Elle n'est pas une neuvième page au sens de la règle 46 —
 * **ni contenu propre, ni entrée dans la navigation, et elle ne lit pas la base** — mais elle
 * est une surface que quelqu'un devra dessiner. »
 *
 * **L'écran de refus du téléphone à la connexion**, S-22 : « la page de connexion étant
 * elle-même hors des huit, **son refus l'était avec elle** ». C'est un état de la page 6, et
 * il vit dans cette page.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **`/connexion` NE SE RENOMME PAS, ET LE COÛT EST HORS DE CE DÉPÔT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Deux autres surfaces la composent depuis leur propre variable de build : `web/` par
 * `PortailDeSession`, `admin/` par `RenvoiVersLaConnexion`, tous deux en
 * `` `${VITE_SITE_URL}/connexion` ``. Changer cette adresse ici **casserait les deux
 * silencieusement** — elles renverraient vers une adresse qui rend la page de refus, ce qui
 * ressemble à une session perdue et non à un chemin faux. C'est la même classe de valeur
 * recopiée que le nom du cookie anti-CSRF (D-087), et pour la même raison : `shared/` est
 * fermé à ce qui n'est pas un contrat.
 */

/** Une page de la liste fermée. */
export type Page = {
  /** Son adresse. Une seule, et elle ne varie pas par environnement. */
  readonly adresse: string;
  /** Son nom dans le menu. Absent quand la page n'est pas dans le menu. */
  readonly menu?: string;
  /** Son nom dans le pied de page. Absent quand elle n'y figure pas. */
  readonly pied?: string;
  /** Le rang de F-69, écrit pour que la table du cahier des charges se relise ici. */
  readonly rang: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
};

/**
 * **Les huit, dans l'ordre de la table de F-69.**
 *
 * **Le menu et le pied de page ne portent PAS les mêmes entrées, et c'est réglé au cahier
 * des charges, pas ici.**
 *
 * F-69 : « **le pied de page porte le chemin vers la page juridique, la page du groupe et
 * l'adresse email affichée. Il ne porte pas le carrousel.** » Trois éléments, liste fermée —
 * et l'adresse email n'est pas une page, donc elle n'est pas dans cette table.
 *
 * F-71 : la page de connexion « **porte le pied de page et pas le menu** » — elle n'est donc
 * ni dans l'un ni dans l'autre en tant qu'entrée : le chemin **vers** elle est une action,
 * présente « depuis chacune des sept autres pages, dans le menu ».
 *
 * **LES LIBELLÉS DE MENU SONT COURTS, ET C'EST UN DÉFAUT CONSTATÉ À L'ÉCRAN QUI LES A
 * RACCOURCIS.** La première forme portait « Pour les structures », « Pour les tuteurs »,
 * « Le produit », « Nous écrire » : six entrées, plus la marque et le bouton d'action, **ne
 * tenaient pas dans la largeur du bandeau**, et le bouton « se connecter » passait à la
 * ligne — donc le chemin d'entrée du produit se retrouvait sous le menu, à 1280 points de
 * large. Le libellé de chaque page est laissé à l'outil de maquettage par F-69 ; celui du
 * MENU doit en plus tenir dans un bandeau, et c'est ce que ces quatre-là font. Les titres
 * complets vivent dans le `h1` de chaque page, où ils ont la place.
 */
export const LES_HUIT_PAGES: readonly Page[] = [
  { rang: 1, adresse: '/', menu: 'Accueil' },
  { rang: 2, adresse: '/structures', menu: 'Structures' },
  { rang: 3, adresse: '/tuteurs', menu: 'Tuteurs' },
  { rang: 4, adresse: '/produit', menu: 'Produit' },
  { rang: 5, adresse: '/contact', menu: 'Contact' },
  // Page 6. **Ni menu, ni pied** : F-71 — « elle porte le pied de page et pas le menu »,
  // et une entrée de menu vers soi-même n'existe pas. Le chemin vers elle est une action.
  { rang: 6, adresse: '/connexion' },
  { rang: 7, adresse: '/mentions-legales', pied: 'Mentions légales' },
  { rang: 8, adresse: '/le-groupe', menu: 'Le groupe', pied: 'Le groupe' },
];

/** L'adresse de la page de connexion, que trois surfaces composent. Voir ci-dessus. */
export const ADRESSE_DE_LA_CONNEXION = '/connexion';

/** L'adresse de l'accueil, où la page de refus renvoie — F-69. */
export const ADRESSE_DE_L_ACCUEIL = '/';
